import { useColorModeSafe } from './ColorModeContext';
import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

// Define theme structure
export interface ThemeColors {
  background: string;
  text: string;
  primary: string;
  secondary: string;
  hover: string;
  pressed?: string;
  disabled?: string;
  accent: string;
  danger: string;
  dangerHover: string;
  success: string;
  successHover: string;
  warning?: string;
  gray: {
    [key: string]: string;
  };
  button: {
    primary: {
      bg: string;
      text: string;
      hover: string;
      pressed?: string;
      disabled?: string;
      solidBg?: string;
      solidHover?: string;
      gradient?: string;
      gradientHover?: string;
      [key: string]: string | undefined;
    };
    secondary: {
      bg: string;
      text: string;
      hover: string;
      pressed?: string;
      disabled?: string;
      transparentBg?: string;
      border?: string;
      [key: string]: string | undefined;
    };
    danger: {
      bg: string;
      text: string;
      hover: string;
      pressed?: string;
      disabled?: string;
      solidBg?: string;
      solidText?: string;
      solidHover?: string;
      [key: string]: string | undefined;
    };
    success?: {
      bg: string;
      text: string;
      hover: string;
      pressed?: string;
      disabled?: string;
      transparentBg?: string;
      border?: string;
      [key: string]: string | undefined;
    };
    warning?: {
      bg: string;
      text: string;
      hover: string;
      pressed?: string;
      disabled?: string;
      solidBg?: string;
      solidText?: string;
      solidHover?: string;
      [key: string]: string | undefined;
    };
    utility?: {
      bg: string;
      text: string;
      hover: string;
      pressed?: string;
      disabled?: string;
      [key: string]: string | undefined;
    };
    add?: {
      bg: string;
      text: string;
      hover: string;
      pressed?: string;
      shadow?: string;
      hoverShadow?: string;
      [key: string]: string | undefined;
    };
    floating?: {
      bg: string;
      text: string;
      hover: string;
      shadow?: string;
      hoverShadow?: string;
      [key: string]: string | undefined;
    };
  };
  components: {
    card: {
      background: string;
      border: string;
      hover: string;
      shadow?: string;
      hoverShadow?: string;
      gradient?: string;
    };
    input: {
      background: string;
      border: string;
      focus: string;
      focusRing?: string;
      placeholder?: string;
      disabled?: string;
    };
    modal: {
      overlay: string;
      background: string;
      backdropBlur?: string;
      border?: string;
      shadow?: string;
    };
    toggleIconButton: {
      background: string;
      text: string;
      hover: string;
      pressed: string;
      disabled: string;
    };
    status?: {
      [key: string]: {
        bg: string;
        text: string;
        border?: string;
      };
    };
    table?: {
      header: string;
      border: string;
      hover: string;
      stripe: string;
    };
    badge?: {
      [key: string]: {
        bg: string;
        text: string;
      };
    };
    divider?: string;
    skeleton?: {
      base: string;
      shimmer: string;
    };
    bottomNav?: {
      background: string;
      backdropBlur: string;
      border: string;
      activeIndicator: string;
      inactiveIcon: string;
      badge: string;
    };
    bottomSheet?: {
      background: string;
      handle: string;
      shadow: string;
    };
    search?: {
      background: string;
      placeholder: string;
      text: string;
      icon: string;
    };
    header?: {
      gradient: string;
      text: string;
      subtext: string;
    };
    pill?: {
      active: {
        bg: string;
        text: string;
        shadow: string;
      };
      inactive: {
        bg: string;
        text: string;
        hover: string;
      };
    };
    menuItem?: {
      imagePlaceholder: string;
      popular: {
        ring: string;
        badge: string;
        icon: string;
      };
      favorite: {
        active: string;
        inactive: string;
      };
      time: {
        icon: string;
        text: string;
      };
    };
    quantityControl?: {
      container: string;
      button: {
        bg: string;
        text: string;
        hover: string;
        shadow: string;
      };
      addButton: {
        bg: string;
        text: string;
        hover: string;
      };
    };
  };
}

// Define the available themes
const themes: { [key: string]: ThemeColors } = {
  light: {
    background: '#F5F3EF',         // Overall background
    text: '#3E3E3E',               // Text color
    primary: '#806464',            // Primary color
    secondary: '#A3B18A',          // Secondary color
    hover: '#F0EAD2',              // Hover state
    pressed: '#8E6E6E',            // Pressed state
    disabled: '#E5E5E5',           // Disabled state
    accent: '#5D6A4E',             // Selected tab color
    danger: '#D99982',             // Danger color (logout)
    dangerHover: '#C18871',        // Danger hover
    success: '#4CAF50',            // Success color
    successHover: '#388E3C',       // Success hover
    warning: '#FFC107',            // Warning color
    gray: {
      50: '#F9F9F9',
      100: '#F0F0F0',
      200: '#E0E0E0',
      300: '#CCCCCC',
      500: '#666666',
      600: '#888888',
      700: '#AAAAAA',
      800: '#CCCCCC',
    },
    button: {
      primary: {
        bg: '#B37A4C',           // Add button (warm amber brown)
        text: '#FFFFFF',         // White text
        hover: '#C98B5F',        // Lighter amber hover
        pressed: '#8C5530',      // Darker pressed state
        disabled: '#E5E5E5',     // Disabled state
      },
      secondary: {
        bg: '#F8EEDC',           // Edit icon button (soft cream)
        text: '#3E3E3E',         // Dark text
        hover: '#F5E9DA',        // Hover state
        pressed: '#EFE5D3',      // Pressed state
        disabled: '#F0F0F0',     // Disabled state
      },
      danger: {
        bg: '#F9D3C6',           // Delete icon button (soft peach)
        text: '#3E3E3E',         // Dark text
        hover: '#F7C9BA',        // Hover state
        pressed: '#F5BFB0',      // Pressed state
        disabled: '#E5E5E5',     // Disabled state
      },
    },
    components: {
      card: {
        background: '#FFFFFF',
        border: '#E0E0E0',
        hover: '#F5E9DA',
      },
      input: {
        background: '#FFFFFF',
        border: '#DDD8D2',
        focus: '#B37A4C',        // Focus color matches primary button
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.5)',
        background: '#FFFFFF',
      },
      toggleIconButton: {
        background: '#F8EEDC',   // Light peach background for layout toggle buttons
        text: '#3E3E3E',         // Dark text/icon color
        hover: '#F3E7D5',        // Hover state
        pressed: '#EAD9C7',      // Pressed state
        disabled: '#F0F0F0',     // Disabled state
      },
    }
  },
  dark: {
     background: '#1F1F1F',         // Deep charcoal background
    text: '#FFFFFF',
    primary: '#A27B5C',            // Warm terracotta brown
    secondary: '#B7B78A',          // Pale olive green
    hover: '#292929',
    pressed: '#8E6E6E',
    disabled: '#555555',
    accent: '#B7B78A',
    danger: '#D99982',
    dangerHover: '#D32F2F',
    success: '#66BB6A',
    successHover: '#388E3C',
    warning: '#FFD54F',
    gray: {
       50: '#555555',
      100: '#444444',
      200: '#3B3B3B',
      300: '#3C3C3C',
      500: '#CCCCCC',
      600: '#AAAAAA',
      700: '#888888',
      800: '#666666',
    },
    button: {
      primary: {
        bg: '#A27B5C',
        text: '#FFFFFF',
        hover: '#3C3C3C',
        pressed: '#8E6E6E',
        disabled: '#555555',
            },
      secondary: {
      bg: '#B7B78A',
        text: '#2B2B2B',
        hover: '#888888',
        pressed: '#6B4F4F',
        disabled: '#444444',
      },
      danger: {
bg: '#FF6B6B',
        text: '#FFFFFF',
        hover: '#FF6B6B',
        pressed: '#D99982',
        disabled: '#555555',      }
    },
    components: {
      card: {
        background: '#2D2D2D',
        border: '#444444',
        hover: '#292929',
      },
      input: {
         background: '#2B2B2B',
        border: '#444444',
        focus: '#A27B5C',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.7)',
        background: '#2D2D2D',
      },
      toggleIconButton: {
        background: '#3B3B3B',   // Darker background for toggle buttons in dark mode
        text: '#DDDDDD',         // Light text/icon color
        hover: '#4A4A4A',        // Hover state
        pressed: '#565656',      // Pressed state
        disabled: '#333333',     // Disabled state
      }
    }
  },
  blue: {
    background: '#ECE5DD', // Light beige
    text: '#111111', // Dark text
    primary: '#075E54', // Deep green
    secondary: '#FFFFFF', // White card/surface
    hover: '#25D366', // Bright green accent
    accent: '#25D366', // Bright green accent
    danger: '#DC2626',
    dangerHover: '#B91C1C',
    success: '#059669',
    successHover: '#047857',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
    },
    button: {
      primary: {
        bg: '#075E54',
        text: '#FFFFFF',
        hover: '#25D366',
      },
      secondary: {
        bg: '#F3F4F6',
        text: '#374151',
        hover: '#E5E7EB',
      },
      danger: {
        bg: '#FEE2E2',
        text: '#DC2626',
        hover: '#FEE2E2',
      }
    },
    components: {
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        hover: '#F3F4F6',
      },
      input: {
        background: '#FFFFFF',
        border: '#D1D5DB',
        focus: '#075E54',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.5)',
        background: '#FFFFFF',
      },
      toggleIconButton: {
        background: '#F3F4F6',
        text: '#374151',
        hover: '#E5E7EB',
        pressed: '#D1D5DB',
        disabled: '#F9FAFB',
      }
    }
  },
  glassmorphism: {
    background: '#0a0e27',         // Deep blue-black background
    text: '#ffffff',              // White text
    primary: '#60a5fa',           // Sky blue
    secondary: '#a78bfa',         // Purple
    hover: 'rgba(96, 165, 250, 0.1)', // Sky blue hover
    pressed: '#3b82f6',           // Blue pressed
    disabled: 'rgba(148, 163, 184, 0.3)', // Semi-transparent disabled
    accent: '#818cf8',            // Indigo accent
    danger: '#f87171',            // Light red
    dangerHover: '#ef4444',       // Red hover
    success: '#34d399',           // Emerald
    successHover: '#10b981',      // Darker emerald
    warning: '#fbbf24',           // Amber
    gray: {
      50: 'rgba(255, 255, 255, 0.05)',
      100: 'rgba(255, 255, 255, 0.1)',
      200: 'rgba(255, 255, 255, 0.2)',
      300: 'rgba(255, 255, 255, 0.3)',
      500: 'rgba(255, 255, 255, 0.5)',
      600: 'rgba(255, 255, 255, 0.6)',
      700: 'rgba(255, 255, 255, 0.7)',
      800: 'rgba(255, 255, 255, 0.8)',
    },
    button: {
      primary: {
        bg: 'rgba(96, 165, 250, 0.2)',
        text: '#60a5fa',
        hover: 'rgba(96, 165, 250, 0.3)',
        pressed: 'rgba(96, 165, 250, 0.4)',
        disabled: 'rgba(148, 163, 184, 0.1)',
      },
      secondary: {
        bg: 'rgba(167, 139, 250, 0.2)',
        text: '#a78bfa',
        hover: 'rgba(167, 139, 250, 0.3)',
        pressed: 'rgba(167, 139, 250, 0.4)',
        disabled: 'rgba(148, 163, 184, 0.1)',
      },
      danger: {
        bg: 'rgba(248, 113, 113, 0.2)',
        text: '#f87171',
        hover: 'rgba(248, 113, 113, 0.3)',
        pressed: 'rgba(248, 113, 113, 0.4)',
        disabled: 'rgba(248, 113, 113, 0.1)',
      }
    },
    components: {
      card: {
        background: 'rgba(255, 255, 255, 0.05)',
        border: 'rgba(255, 255, 255, 0.1)',
        hover: 'rgba(255, 255, 255, 0.08)',
      },
      input: {
        background: 'rgba(255, 255, 255, 0.05)',
        border: 'rgba(255, 255, 255, 0.1)',
        focus: '#60a5fa',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.8)',
        background: 'rgba(15, 23, 42, 0.95)',
      },
      toggleIconButton: {
        background: 'rgba(96, 165, 250, 0.1)',
        text: '#60a5fa',
        hover: 'rgba(96, 165, 250, 0.2)',
        pressed: 'rgba(96, 165, 250, 0.3)',
        disabled: 'rgba(148, 163, 184, 0.1)',
      }
    }
  },
  gradient: {
    background: '#0f172a',         // Dark slate background
    text: '#f1f5f9',              // Light text
    primary: '#64748b',           // Slate gray (neutral)
    secondary: '#94a3b8',         // Light slate gray
    hover: 'rgba(100, 116, 139, 0.1)', // Gray hover with transparency
    pressed: '#475569',           // Darker gray
    disabled: 'rgba(148, 163, 184, 0.2)', // Semi-transparent disabled
    accent: '#94a3b8',            // Light gray accent
    danger: '#ef4444',            // Red
    dangerHover: '#dc2626',       // Darker red
    success: '#10b981',           // Green
    successHover: '#059669',      // Darker green
    warning: '#f59e0b',           // Amber
    gray: {
      50: 'rgba(241, 245, 249, 0.05)',
      100: 'rgba(226, 232, 240, 0.1)',
      200: 'rgba(203, 213, 225, 0.2)',
      300: 'rgba(148, 163, 184, 0.3)',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
    },
    button: {
      primary: {
        bg: '#64748b',  // Slate gray
        text: '#FFFFFF',
        hover: '#475569',  // Darker slate
        pressed: '#334155',  // Even darker
        disabled: 'rgba(148, 163, 184, 0.2)',
      },
      secondary: {
        bg: 'rgba(100, 116, 139, 0.1)',
        text: '#64748b',
        hover: 'rgba(100, 116, 139, 0.2)',
        pressed: 'rgba(100, 116, 139, 0.3)',
        disabled: 'rgba(148, 163, 184, 0.1)',
      },
      danger: {
        bg: 'rgba(239, 68, 68, 0.1)',
        text: '#ef4444',
        hover: 'rgba(239, 68, 68, 0.2)',
        pressed: 'rgba(239, 68, 68, 0.3)',
        disabled: 'rgba(239, 68, 68, 0.05)',
      }
    },
    components: {
      card: {
        background: '#FFFDFB',     // Subtle warm white (soft almond tone)
        border: '#E6DDD7',         // Light beige border
        hover: '#F4EAE3',          // Very light hover tone
      },
      input: {
        background: 'rgba(30, 41, 59, 0.5)',
        border: 'rgba(148, 163, 184, 0.2)',
        focus: '#a855f7',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.7)',
        background: 'rgba(15, 23, 42, 0.95)',
      },
      toggleIconButton: {
        background: 'rgba(168, 85, 247, 0.1)',
        text: '#a855f7',
        hover: 'rgba(168, 85, 247, 0.2)',
        pressed: 'rgba(168, 85, 247, 0.3)',
        disabled: 'rgba(148, 163, 184, 0.1)',
      }
    }
  },
  googleNews: {
    background: '#F8F9FA',       // Light gray background (Google News)
    text: '#202124',             // Dark text
    primary: '#1A73E8',          // Google blue
    secondary: '#5F6368',        // Gray
    hover: '#F1F3F4',            // Light hover state
    pressed: '#D2E3FC',          // Pressed state
    disabled: '#E0E0E0',         // Disabled state
    accent: '#1A73E8',           // Selected tab color (Google blue)
    danger: '#D93025',           // Google red
    dangerHover: '#C5221F',      // Darker red hover
    success: '#188038',          // Google green
    successHover: '#0F703C',     // Darker green hover
    warning: '#F9AB00',          // Google yellow/amber
    gray: {
      50: '#F8F9FA',
      100: '#F1F3F4',
      200: '#E8EAED',
      300: '#DADCE0',
      500: '#5F6368',
      600: '#3C4043',
      700: '#202124',
      800: '#1F1F1F',
    },
    button: {
      primary: {
        bg: '#1A73E8',           // Google blue
        text: '#FFFFFF',         // White text
        hover: '#2B7DE9',        // Lighter blue hover
        pressed: '#174EA6',      // Darker blue pressed
        disabled: '#E0E0E0',     // Disabled state
      },
      secondary: {
        bg: '#F1F3F4',           // Light gray (Google-style)
        text: '#202124',         // Dark text
        hover: '#E8EAED',        // Hover state
        pressed: '#DADCE0',      // Pressed state
        disabled: '#F5F5F5',     // Disabled state
      },
      utility: {                 // Style for edit/view toggle buttons
        bg: '#F1F3F4',           // Light gray (Google-style utility button)
        text: '#202124',         // Google dark text
        hover: '#E8EAED',        // Hover state
        pressed: '#DADCE0',      // Pressed state
        disabled: '#E0E0E0',     // Disabled state
      },
      danger: {
        bg: '#FCE8E6',           // Light red background (Google-style)
        text: '#D93025',         // Red text (Google-style)
        hover: '#F8DAD9',        // Hover state (Google-style)
        pressed: '#D93025',      // Pressed state (Google-style)
        disabled: '#E0E0E0',     // Disabled state
      },
    },
    components: {
      card: {
        background: '#FFFFFF',   // White cards
        border: '#E0E0E0',       // Light gray border
        hover: '#F1F3F4',        // Light hover effect
      },
      input: {
        background: '#FFFFFF',   // White input background
        border: '#DADCE0',       // Google's standard input border
        focus: '#1A73E8',        // Focus border color (Google blue)
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.32)', // Google-style overlay
        background: '#FFFFFF',    // White background
      },
      toggleIconButton: {
        background: '#F1F3F4',    // Light gray background for toggle buttons
        text: '#5F6368',          // Gray icon color
        hover: '#E8EAED',         // Hover state
        pressed: '#DADCE0',       // Pressed state
        disabled: '#F5F5F5',      // Disabled state
      },
    }
  },
  nextgen: {
    background: '#FAFAFA',        // Very light gray background
    text: '#1F2937',             // Dark slate text
    primary: '#6366F1',          // Indigo primary
    secondary: '#F3F4F6',        // Light gray
    hover: '#EEF2FF',            // Light indigo hover
    pressed: '#4F46E5',          // Darker indigo pressed
    disabled: '#E5E7EB',         // Light gray disabled
    accent: '#6366F1',           // Indigo accent
    danger: '#EF4444',           // Red danger
    dangerHover: '#DC2626',      // Darker red hover
    success: '#10B981',          // Emerald success
    successHover: '#059669',     // Darker emerald hover
    warning: '#F59E0B',          // Amber warning
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
    },
    button: {
      primary: {
        bg: '#6366F1',           // Indigo
        text: '#FFFFFF',         // White text
        hover: '#7C3AED',        // Purple hover
        pressed: '#4F46E5',      // Darker indigo pressed
        disabled: '#E5E7EB',     // Light gray disabled
      },
      secondary: {
        bg: '#F3F4F6',           // Light gray
        text: '#374151',         // Dark gray text
        hover: '#E5E7EB',        // Hover state
        pressed: '#D1D5DB',      // Pressed state
        disabled: '#F9FAFB',     // Disabled state
      },
      danger: {
        bg: '#FEE2E2',           // Light red background
        text: '#EF4444',         // Red text
        hover: '#FECACA',        // Hover state
        pressed: '#DC2626',      // Pressed state
        disabled: '#E5E7EB',     // Disabled state
      },
    },
    components: {
      card: {
        background: '#FFFFFF',   // White cards
        border: '#E5E7EB',       // Light gray border
        hover: '#F9FAFB',        // Light hover effect
      },
      input: {
        background: '#FFFFFF',   // White input background
        border: '#D1D5DB',       // Gray input border
        focus: '#6366F1',        // Focus border color (indigo)
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.4)', // Dark overlay
        background: '#FFFFFF',    // White background
      },
      toggleIconButton: {
        background: '#F3F4F6',    // Light gray background
        text: '#6B7280',          // Gray icon color
        hover: '#E5E7EB',         // Hover state
        pressed: '#D1D5DB',       // Pressed state
        disabled: '#F9FAFB',      // Disabled state
      },
    }
  },
  nextgenDark: {
    background: '#0F172A',       // Very dark slate background
    text: '#F1F5F9',            // Very light slate text
    primary: '#818CF8',         // Light indigo primary
    secondary: '#1E293B',       // Dark slate secondary
    hover: '#312E81',           // Dark indigo hover
    pressed: '#6366F1',         // Indigo pressed
    disabled: '#374151',        // Dark gray disabled
    accent: '#818CF8',          // Light indigo accent
    danger: '#F87171',          // Light red danger
    dangerHover: '#EF4444',     // Red hover
    success: '#34D399',         // Light emerald success
    successHover: '#10B981',    // Emerald hover
    warning: '#FBBF24',         // Light amber warning
    gray: {
      50: '#1E293B',
      100: '#334155',
      200: '#475569',
      300: '#64748B',
      500: '#94A3B8',
      600: '#CBD5E1',
      700: '#E2E8F0',
      800: '#F1F5F9',
    },
    button: {
      primary: {
        bg: '#818CF8',           // Light indigo
        text: '#0F172A',         // Very dark text
        hover: '#A5B4FC',        // Lighter indigo hover
        pressed: '#6366F1',      // Indigo pressed
        disabled: '#374151',     // Dark gray disabled
      },
      secondary: {
        bg: '#1E293B',           // Dark slate
        text: '#F1F5F9',         // Light text
        hover: '#334155',        // Hover state
        pressed: '#475569',      // Pressed state
        disabled: '#0F172A',     // Disabled state
      },
      danger: {
        bg: '#7F1D1D',           // Dark red background
        text: '#F87171',         // Light red text
        hover: '#991B1B',        // Hover state
        pressed: '#B91C1C',      // Pressed state
        disabled: '#374151',     // Disabled state
      },
    },
    components: {
      card: {
        background: '#1E293B',   // Dark slate cards
        border: '#334155',       // Dark border
        hover: '#334155',        // Hover effect
      },
      input: {
        background: '#0F172A',   // Very dark input background
        border: '#334155',       // Dark border
        focus: '#818CF8',        // Focus border color (light indigo)
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.6)', // Dark overlay
        background: '#1E293B',    // Dark slate background
      },
      toggleIconButton: {
        background: '#334155',    // Dark background
        text: '#94A3B8',          // Light gray icon color
        hover: '#475569',         // Hover state
        pressed: '#64748B',       // Pressed state
        disabled: '#1E293B',      // Disabled state
      },
    }
  },
  zenBistro: {
    background: '#F8F4EF',       // soft almond base
    text: '#2E2A27',             // deep espresso
    primary: '#C97B63',          // terracotta orange (warm, appetizing)
    secondary: '#F4E3D7',        // peach cream
    hover: '#E2C7B7',            // warm almond hover
    pressed: '#A85D49',          // deeper terracotta
    disabled: '#DDD4CE',         // muted beige
    accent: '#7A9E7E',           // calming sage green
    danger: '#D94F4F',
    dangerHover: '#BF3C3C',
    success: '#55A868',
    successHover: '#3F8B51',
    warning: '#F2C94C',
    gray: {
      50: '#FCFAF8',
      100: '#F0ECE7',
      200: '#DFD7D0',
      300: '#CBBEB5',
      500: '#85776F',
      600: '#6F625B',
      700: '#5B504B',
      800: '#3E3632',
    },
    button: {
      primary: {
        bg: '#C97B63',
        text: '#FFFFFF',
        hover: '#DE8B72',
        pressed: '#A85D49',
        disabled: '#E8DED7',
      },
      secondary: {
        bg: '#F4E3D7',
        text: '#2E2A27',
        hover: '#EAD6CB',
        pressed: '#DBC1B3',
        disabled: '#E8E2DC',
      },
      danger: {
        bg: '#F7C1BD',
        text: '#8B1E1E',
        hover: '#F4B1AD',
        pressed: '#E29995',
        disabled: '#EBD9D9',
      },
    },
    components: {
      card: {
        background: '#FFFFFF',
        border: '#E6DDD7',
        hover: '#F7EEE9',
      },
      input: {
        background: '#FFFFFF',
        border: '#D8CFC8',
        focus: '#C97B63',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.5)',
        background: '#FFFFFF',
      },
      toggleIconButton: {
        background: '#F4E3D7',
        text: '#2E2A27',
        hover: '#EAD6CB',
        pressed: '#DBC1B3',
        disabled: '#E8E2DC',
      },
    }
  },
  modernFlow: {
    background: '#FAFAFA',        // Clean light background
    text: '#111827',              // Rich dark text
    primary: '#6366F1',           // Vibrant indigo
    secondary: '#A78BFA',         // Soft purple
    hover: '#EEF2FF',             // Light indigo hover
    pressed: '#4F46E5',           // Deep indigo pressed
    disabled: '#E5E7EB',          // Subtle gray disabled
    accent: '#818CF8',            // Light indigo accent
    danger: '#EF4444',            // Modern red
    dangerHover: '#DC2626',       // Darker red hover
    success: '#10B981',           // Fresh emerald
    successHover: '#059669',      // Darker emerald hover
    warning: '#F59E0B',           // Warm amber
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
    },
    button: {
      primary: {
        bg: 'linear-gradient(135deg, #6366F1 0%, #A78BFA 100%)', // Gradient primary
        text: '#FFFFFF',
        hover: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', // Darker gradient hover
        pressed: '#4F46E5',
        disabled: '#E5E7EB',
        // Additional properties for solid fallback
        solidBg: '#6366F1',
        solidHover: '#4F46E5',
      },
      secondary: {
        bg: '#F3F4F6',
        text: '#374151',
        hover: '#E5E7EB',
        pressed: '#D1D5DB',
        disabled: '#F9FAFB',
      },
      danger: {
        bg: '#FEE2E2',
        text: '#EF4444',
        hover: '#FECACA',
        pressed: '#DC2626',
        disabled: '#E5E7EB',
      },
      // Special button styles for modern UI elements
      add: {
        bg: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)', // Light indigo gradient
        text: '#4C1D95',          // Deep purple text
        hover: 'linear-gradient(135deg, #C7D2FE 0%, #A5B4FC 100%)', // Darker gradient
        pressed: 'linear-gradient(135deg, #A5B4FC 0%, #818CF8 100%)',
        shadow: '0 2px 8px rgba(199, 210, 254, 0.5)',
        hoverShadow: '0 4px 12px rgba(199, 210, 254, 0.6)',
      },
      floating: {
        bg: 'linear-gradient(135deg, #6366F1 0%, #7C3AED 100%)', // Indigo to purple
        text: '#FFFFFF',
        hover: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)',
        shadow: '0 10px 25px rgba(99, 102, 241, 0.3)',
        hoverShadow: '0 15px 35px rgba(99, 102, 241, 0.4)',
      },
    },
    components: {
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        hover: '#F9FAFB',
        shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        hoverShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      input: {
        background: '#FFFFFF',
        border: '#D1D5DB',
        focus: '#6366F1',
        focusRing: '0 0 0 3px rgba(99, 102, 241, 0.1)',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.5)',
        background: '#FFFFFF',
        backdropBlur: 'blur(4px)',
      },
      toggleIconButton: {
        background: '#F3F4F6',
        text: '#6B7280',
        hover: '#E5E7EB',
        pressed: '#D1D5DB',
        disabled: '#F9FAFB',
      },
      bottomNav: {
        background: 'rgba(255, 255, 255, 0.8)',
        backdropBlur: 'blur(12px)',
        border: 'rgba(229, 231, 235, 0.5)',
        activeIndicator: '#6366F1',
        inactiveIcon: '#9CA3AF',
        badge: '#EF4444',
      },
      bottomSheet: {
        background: '#FFFFFF',
        handle: '#E5E7EB',
        shadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
      },
      search: {
        background: 'rgba(255, 255, 255, 0.2)',
        placeholder: 'rgba(255, 255, 255, 0.7)',
        text: '#FFFFFF',
        icon: 'rgba(255, 255, 255, 0.7)',
      },
      header: {
        gradient: 'linear-gradient(135deg, #6366F1 0%, #7C3AED 100%)',
        text: '#FFFFFF',
        subtext: 'rgba(255, 255, 255, 0.8)',
      },
      pill: {
        active: {
          bg: 'linear-gradient(135deg, #6366F1 0%, #7C3AED 100%)',
          text: '#FFFFFF',
          shadow: '0 4px 6px rgba(99, 102, 241, 0.25)',
        },
        inactive: {
          bg: '#F3F4F6',
          text: '#6B7280',
          hover: '#E5E7EB',
        },
      },
      menuItem: {
        imagePlaceholder: 'linear-gradient(135deg, #E0E7FF 0%, #DDD6FE 100%)',
        popular: {
          ring: '#FCD34D',
          badge: '#FCD34D',
          icon: '#F59E0B',
        },
        favorite: {
          active: '#EF4444',
          inactive: '#9CA3AF',
        },
        time: {
          icon: '#9CA3AF',
          text: '#6B7280',
        },
      },
      quantityControl: {
        container: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
        button: {
          bg: '#FFFFFF',
          text: '#6B7280',
          hover: '#F9FAFB',
          shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
        addButton: {
          bg: '#6366F1',
          text: '#FFFFFF',
          hover: '#4F46E5',
        },
      },
    }
  },

  // 🛒 SHOPIFY FRESH THEME - Clean e-commerce aesthetic
  shopifyFresh: {
    background: '#F7F8FA',        // Shopify light gray
    text: '#202223',              // Rich black text
    primary: '#00A96E',           // Shopify green
    secondary: '#F6F6F7',         // Very light gray
    hover: '#E1F5FE',             // Light cyan hover
    pressed: '#008060',           // Darker green pressed
    disabled: '#E0E0E0',          // Light gray disabled
    accent: '#6C5CE7',            // Soft purple accent
    danger: '#D72C0D',            // Shopify red
    dangerHover: '#BF2600',       // Darker red hover
    success: '#008060',           // Shopify success green
    successHover: '#004C3F',      // Darker success hover
    warning: '#FFC453',           // Shopify yellow
    gray: {
      50: '#F7F8FA',
      100: '#F6F6F7',
      200: '#E1F5FE',
      300: '#C7C8CC',
      500: '#6D7175',
      600: '#202223',
      700: '#1A1C1D',
      800: '#0C0D0E',
    },
    button: {
      primary: {
        bg: '#00A96E',            // Shopify green
        text: '#FFFFFF',          // White text
        hover: '#008060',         // Darker green hover
        pressed: '#004C3F',       // Darkest green pressed
        disabled: '#E0E0E0',      // Light gray disabled
      },
      secondary: {
        bg: '#F6F6F7',            // Very light gray
        text: '#202223',          // Dark text
        hover: '#E1F5FE',         // Light cyan hover
        pressed: '#C7C8CC',       // Gray pressed
        disabled: '#F7F8FA',      // Light disabled
      },
      danger: {
        bg: '#FED7D2',            // Light red background
        text: '#D72C0D',          // Red text
        hover: '#FEB2B2',         // Red hover
        pressed: '#BF2600',       // Dark red pressed
        disabled: '#E0E0E0',      // Light gray disabled
      },
    },
    components: {
      card: {
        background: '#FFFFFF',    // Pure white cards
        border: '#E1E3E5',        // Light border
        hover: '#F6F6F7',         // Very light hover
      },
      input: {
        background: '#FFFFFF',    // White input
        border: '#C7C8CC',        // Gray border
        focus: '#00A96E',         // Green focus
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.4)',
        background: '#FFFFFF',
      },
      toggleIconButton: {
        background: '#F6F6F7',
        text: '#6D7175',
        hover: '#E1F5FE',
        pressed: '#C7C8CC',
        disabled: '#F7F8FA',
      },
    }
  },

  // 🎯 CLEAN SLATE - Inspired by Notion's minimal design excellence
  cleanSlate: {
    background: '#FFFFFF',            // Pure white background (like Notion)
    text: '#37352F',                  // Warm dark gray for excellent readability
    primary: '#2F81F7',               // Soft blue primary (trustworthy, professional)
    secondary: '#F7F6F3',             // Very light warm gray for surfaces
    hover: '#F1F1EF',                 // Subtle hover states
    pressed: '#2B6CB8',               // Darker blue for pressed states
    disabled: '#E9E9E7',              // Light gray for disabled states
    accent: '#16A085',                // Subtle teal for success states (fresh, natural)
    danger: '#E55E5E',                // Soft red for danger states
    dangerHover: '#D73E3E',           // Darker red hover
    success: '#16A085',               // Teal for success (matches accent)
    successHover: '#138B7A',          // Darker teal hover
    warning: '#D9910C',               // Warm amber warning
    gray: {
      50: '#FBFBFA',
      100: '#F7F6F3',
      200: '#F1F1EF',
      300: '#E9E9E7',
      500: '#A8A29E',
      600: '#6C6B65',
      700: '#37352F',
      800: '#2D2C28',
    },
    button: {
      primary: {
        bg: '#2F81F7',              // Soft blue primary
        text: '#FFFFFF',            // White text for contrast
        hover: '#4B91F8',           // Light blue hover
        pressed: '#2B6CB8',         // Dark blue pressed
        disabled: '#E9E9E7',        // Light gray disabled
      },
      secondary: {
        bg: '#F7F6F3',              // Light warm gray secondary
        text: '#37352F',            // Dark gray text
        hover: '#F1F1EF',           // Light hover
        pressed: '#E9E9E7',         // Gray pressed
        disabled: '#FBFBFA',        // Very light disabled
      },
      danger: {
        bg: '#FDF2F2',              // Light red background
        text: '#E55E5E',            // Soft red text
        hover: '#FCE8E8',           // Light red hover
        pressed: '#D73E3E',         // Dark red pressed
        disabled: '#E9E9E7',        // Light gray disabled
      },
    },
    components: {
      card: {
        background: '#FFFFFF',      // Pure white cards
        border: '#F1F1EF',          // Very subtle borders
        hover: '#FBFBFA',           // Barely visible hover
        shadow: '0 1px 3px rgba(0, 0, 0, 0.04)', // Extremely subtle shadow
      },
      input: {
        background: '#FFFFFF',      // White input backgrounds
        border: '#E9E9E7',          // Light gray borders
        focus: '#2F81F7',           // Blue focus (brand consistency)
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.15)', // Very light overlay (Notion-style)
        background: '#FFFFFF',      // White modal background
      },
      toggleIconButton: {
        background: '#F7F6F3',      // Light gray toggle buttons
        text: '#A8A29E',            // Gray icons
        hover: '#F1F1EF',           // Light hover
        pressed: '#E9E9E7',         // Gray pressed
        disabled: '#FBFBFA',        // Very light disabled
      },
    }
  },

  // 🏔️ ARCTIC FRESH - Inspired by Figma's "Arctic Reflection" palette
  arcticFresh: {
    background: '#F4FCFB',            // Very light mint background
    text: '#243C4C',                  // Deep blue-gray for excellent readability
    primary: '#5289AD',               // Soft blue primary (professional, calm)
    secondary: '#ACBCBF',             // Light blue-gray for surfaces
    hover: '#EFF7F6',                 // Subtle mint hover
    pressed: '#4A7A98',               // Darker blue for pressed states
    disabled: '#E8F0EF',              // Light mint-gray for disabled
    accent: '#698696',                // Steel blue accent (natural, fresh)
    danger: '#D73E3E',                // Soft red for danger states
    dangerHover: '#C12E2E',           // Darker red hover
    success: '#16A085',               // Teal for success
    successHover: '#138B7A',          // Darker teal hover
    warning: '#D9910C',               // Warm amber warning
    gray: {
      50: '#F8FDFC',
      100: '#F4FCFB',
      200: '#EFF7F6',
      300: '#E8F0EF',
      500: '#ACBCBF',
      600: '#698696',
      700: '#243C4C',
      800: '#1A2B36',
    },
    button: {
      primary: {
        bg: '#5289AD',              // Soft blue primary
        text: '#FFFFFF',            // White text for contrast
        hover: '#6B9BBF',           // Light blue hover
        pressed: '#4A7A98',         // Dark blue pressed
        disabled: '#E8F0EF',        // Light disabled
      },
      secondary: {
        bg: '#ACBCBF',              // Light blue-gray secondary
        text: '#243C4C',            // Dark text
        hover: '#9BB0B4',           // Blue-gray hover
        pressed: '#8EA5AA',         // Darker gray pressed
        disabled: '#F4FCFB',        // Very light disabled
      },
      danger: {
        bg: '#FCF0F0',              // Light red background
        text: '#D73E3E',            // Red text
        hover: '#F9E6E6',           // Light red hover
        pressed: '#C12E2E',         // Dark red pressed
        disabled: '#E8F0EF',        // Light disabled
      },
    },
    components: {
      card: {
        background: '#FFFFFF',      // Pure white cards
        border: '#EFF7F6',          // Mint borders
        hover: '#F8FDFC',           // Light mint hover
        shadow: '0 1px 3px rgba(0, 0, 0, 0.05)', // Subtle shadow
      },
      input: {
        background: '#FFFFFF',      // White inputs
        border: '#E8F0EF',          // Light borders
        focus: '#5289AD',           // Blue focus
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.12)', // Very light overlay
        background: '#FFFFFF',      // White modal
      },
      toggleIconButton: {
        background: '#ACBCBF',      // Blue-gray toggle
        text: '#698696',            // Steel icons
        hover: '#9BB0B4',           // Light hover
        pressed: '#8EA5AA',         // Gray pressed
        disabled: '#F4FCFB',        // Light disabled
      },
    }
  },

  // 🏛️ SOFT LINEN - Inspired by Figma's premium minimal palettes
  softLinen: {
    background: '#F6F5F5',            // Soft off-white background
    text: '#4A413C',                  // Warm dark brown for readability
    primary: '#BB6C43',               // Warm amber primary (inviting, premium)
    secondary: '#E8C09A',             // Light cream for surfaces
    hover: '#F0EFEF',                 // Light gray hover
    pressed: '#A85D3A',               // Darker amber for pressed
    disabled: '#EBEFEE',              // Light gray disabled
    accent: '#C8906D',                // Warm copper accent (natural, earthy)
    danger: '#D73E3E',                // Soft red for danger
    dangerHover: '#C12E2E',           // Darker red hover
    success: '#6B8E23',               // Olive green for success (natural)
    successHover: '#5A7A1E',          // Darker olive hover
    warning: '#D9910C',               // Warm amber warning
    gray: {
      50: '#FDFCFC',
      100: '#F6F5F5',
      200: '#F0EFEF',
      300: '#EBEFEE',
      500: '#ABA19F',
      600: '#6B635F',
      700: '#4A413C',
      800: '#3A322E',
    },
    button: {
      primary: {
        bg: '#BB6C43',              // Warm amber primary
        text: '#FFFFFF',            // White text
        hover: '#D07E55',           // Light amber hover
        pressed: '#A85D3A',         // Dark amber pressed
        disabled: '#EBEFEE',        // Light disabled
      },
      secondary: {
        bg: '#E8C09A',              // Light cream secondary
        text: '#4A413C',            // Dark brown text
        hover: '#E0B48A',           // Cream hover
        pressed: '#D6A777',         // Darker cream pressed
        disabled: '#F6F5F5',        // Light disabled
      },
      danger: {
        bg: '#FCF0F0',              // Light red background
        text: '#D73E3E',            // Red text
        hover: '#F9E6E6',           // Light red hover
        pressed: '#C12E2E',         // Dark red pressed
        disabled: '#EBEFEE',        // Light disabled
      },
    },
    components: {
      card: {
        background: '#FFFFFF',      // Pure white cards
        border: '#F0EFEF',          // Light borders
        hover: '#FDFCFC',           // Barely visible hover
        shadow: '0 1px 3px rgba(0, 0, 0, 0.06)', // Subtle warm shadow
      },
      input: {
        background: '#FFFFFF',      // White inputs
        border: '#EBEFEE',          // Light borders
        focus: '#BB6C43',           // Amber focus
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.10)', // Very light overlay
        background: '#FFFFFF',      // White modal
      },
      toggleIconButton: {
        background: '#E8C09A',      // Cream toggle
        text: '#ABA19F',            // Gray icons
        hover: '#E0B48A',           // Cream hover
        pressed: '#D6A777',         // Darker cream pressed
        disabled: '#F6F5F5',        // Light disabled
      },
    }
  },

  // 💎 SILVER WHISPER - Inspired by modern design systems
  silverWhisper: {
    background: '#EFF3F6',            // Light silver-blue background
    text: '#2D3748',                  // Dark slate for readability
    primary: '#4299E1',               // Clean blue primary (trustworthy, modern)
    secondary: '#F7FAFC',             // Very light blue-white for surfaces
    hover: '#E2E8F0',                 // Light slate hover
    pressed: '#3182CE',               // Darker blue for pressed
    disabled: '#E2E8F0',              // Light gray disabled
    accent: '#38B2AC',                // Teal accent (fresh, modern)
    danger: '#E53E3E',                // Clean red for danger
    dangerHover: '#C53030',           // Darker red hover
    success: '#38A169',               // Clean green for success
    successHover: '#2F855A',          // Darker green hover
    warning: '#D69E2E',               // Clean amber warning
    gray: {
      50: '#F7FAFC',
      100: '#EDF2F7',
      200: '#E2E8F0',
      300: '#CBD5E0',
      500: '#A0AEC0',
      600: '#718096',
      700: '#2D3748',
      800: '#1A202C',
    },
    button: {
      primary: {
        bg: '#4299E1',              // Clean blue primary
        text: '#FFFFFF',            // White text
        hover: '#63B3ED',           // Light blue hover
        pressed: '#3182CE',         // Dark blue pressed
        disabled: '#E2E8F0',        // Light disabled
      },
      secondary: {
        bg: '#F7FAFC',              // Light blue-white secondary
        text: '#2D3748',            // Dark slate text
        hover: '#EDF2F7',           // Light hover
        pressed: '#E2E8F0',         // Gray pressed
        disabled: '#F7FAFC',        // Very light disabled
      },
      danger: {
        bg: '#FED7D7',              // Light red background
        text: '#E53E3E',            // Red text
        hover: '#FEB2B2',           // Light red hover
        pressed: '#C53030',         // Dark red pressed
        disabled: '#E2E8F0',        // Light disabled
      },
    },
    components: {
      card: {
        background: '#FFFFFF',      // Pure white cards
        border: '#E2E8F0',          // Light slate borders
        hover: '#F7FAFC',           // Light hover
        shadow: '0 1px 3px rgba(0, 0, 0, 0.08)', // Clean shadow
      },
      input: {
        background: '#FFFFFF',      // White inputs
        border: '#CBD5E0',          // Gray borders
        focus: '#4299E1',           // Blue focus
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.15)', // Light overlay
        background: '#FFFFFF',      // White modal
      },
      toggleIconButton: {
        background: '#F7FAFC',      // Light toggle
        text: '#A0AEC0',            // Gray icons
        hover: '#EDF2F7',           // Light hover
        pressed: '#E2E8F0',         // Gray pressed
        disabled: '#F7FAFC',        // Light disabled
      },
    }
  },

  // 🍯 GOLDEN CREAM - Inspired by the food app design you shared
  goldenCream: {
    background: '#FBF9F6',            // Warm cream background (like the image)
    text: '#3D3832',                  // Dark warm brown for readability
    primary: '#F4B942',               // Golden yellow primary (like the buttons)
    secondary: '#F7F4EF',             // Light cream for surfaces
    hover: '#F5F2ED',                 // Subtle cream hover
    pressed: '#E5A835',               // Darker golden for pressed states
    disabled: '#F0EDE8',              // Light cream-gray for disabled
    accent: '#E8B04B',                // Warm golden accent (food-focused)
    danger: '#E74C3C',                // Warm red for danger states
    dangerHover: '#D73527',           // Darker red hover
    success: '#27AE60',               // Fresh green for success
    successHover: '#229954',          // Darker green hover
    warning: '#F39C12',               // Warm orange warning
    gray: {
      50: '#FEFCFA',
      100: '#FBF9F6',
      200: '#F5F2ED',
      300: '#F0EDE8',
      500: '#B8B5B0',
      600: '#7A7772',
      700: '#3D3832',
      800: '#2A251F',
    },
    button: {
      primary: {
        bg: '#F4B942',              // Golden yellow primary (matches the image)
        text: '#FFFFFF',            // White text for contrast
        hover: '#F5C55A',           // Light golden hover
        pressed: '#E5A835',         // Dark golden pressed
        disabled: '#F0EDE8',        // Light cream disabled
      },
      secondary: {
        bg: '#F7F4EF',              // Light cream secondary
        text: '#3D3832',            // Dark brown text
        hover: '#F2EFE9',           // Cream hover
        pressed: '#EDEAE4',         // Darker cream pressed
        disabled: '#FBF9F6',        // Very light disabled
      },
      danger: {
        bg: '#FDEDEB',              // Light red background
        text: '#E74C3C',            // Red text
        hover: '#FBE3E1',           // Light red hover
        pressed: '#D73527',         // Dark red pressed
        disabled: '#F0EDE8',        // Light disabled
      },
    },
    components: {
      card: {
        background: '#FFFFFF',      // Pure white cards (like the image)
        border: '#F5F2ED',          // Cream borders
        hover: '#FEFCFA',           // Light cream hover
        shadow: '0 2px 8px rgba(0, 0, 0, 0.04)', // Very subtle warm shadow
      },
      input: {
        background: '#FFFFFF',      // White inputs
        border: '#F0EDE8',          // Light cream borders
        focus: '#F4B942',           // Golden focus
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.08)', // Very light overlay
        background: '#FFFFFF',      // White modal
      },
      toggleIconButton: {
        background: '#F7F4EF',      // Cream toggle
        text: '#B8B5B0',            // Light brown icons
        hover: '#F2EFE9',           // Light hover
        pressed: '#EDEAE4',         // Cream pressed
        disabled: '#FBF9F6',        // Very light disabled
      },
    }
  },

  // 🟣 LAVENDER DELIGHT - Inspired by the purple ice cream app design you shared
  lavenderDelight: {
    background: '#F8F7FF',            // Very light lavender background
    text: '#2D2A3D',                  // Dark purple-gray for readability
    primary: '#7C4DFF',               // Beautiful purple primary (like the app)
    secondary: '#F3F2FF',             // Light lavender for surfaces
    hover: '#F0EFFF',                 // Subtle lavender hover
    pressed: '#6B42E0',               // Darker purple for pressed states
    disabled: '#EEECFF',              // Light lavender-gray for disabled
    accent: '#9C88FF',                // Soft purple accent (dessert-focused)
    danger: '#FF4B6E',                // Soft pink-red for danger states
    dangerHover: '#E8405C',           // Darker pink-red hover
    success: '#4CAF50',               // Fresh green for success
    successHover: '#45A049',          // Darker green hover
    warning: '#FF9800',               // Warm orange warning
    gray: {
      50: '#FEFEFF',
      100: '#F8F7FF',
      200: '#F0EFFF',
      300: '#EEECFF',
      500: '#B5B3C4',
      600: '#7A7787',
      700: '#2D2A3D',
      800: '#1F1D2B',
    },
    button: {
      primary: {
        bg: '#7C4DFF',              // Beautiful purple primary (matches the image)
        text: '#FFFFFF',            // White text for contrast
        hover: '#8F66FF',           // Light purple hover
        pressed: '#6B42E0',         // Dark purple pressed
        disabled: '#EEECFF',        // Light lavender disabled
      },
      secondary: {
        bg: '#F3F2FF',              // Light lavender secondary
        text: '#2D2A3D',            // Dark purple text
        hover: '#EDEAFF',           // Lavender hover
        pressed: '#E5E1FF',         // Darker lavender pressed
        disabled: '#F8F7FF',        // Very light disabled
      },
      danger: {
        bg: '#FFF0F2',              // Light pink background
        text: '#FF4B6E',            // Pink-red text
        hover: '#FFE6EA',           // Light pink hover
        pressed: '#E8405C',         // Dark pink pressed
        disabled: '#EEECFF',        // Light disabled
      },
    },
    components: {
      card: {
        background: '#FFFFFF',      // Pure white cards (like the image)
        border: '#F0EFFF',          // Lavender borders
        hover: '#FEFEFF',           // Light lavender hover
        shadow: '0 4px 12px rgba(124, 77, 255, 0.08)', // Subtle purple shadow
      },
      input: {
        background: '#FFFFFF',      // White inputs
        border: '#EEECFF',          // Light lavender borders
        focus: '#7C4DFF',           // Purple focus
      },
      modal: {
        overlay: 'rgba(124, 77, 255, 0.10)', // Light purple overlay
        background: '#FFFFFF',      // White modal
      },
      toggleIconButton: {
        background: '#F3F2FF',      // Lavender toggle
        text: '#B5B3C4',            // Light purple icons
        hover: '#EDEAFF',           // Light hover
        pressed: '#E5E1FF',         // Lavender pressed
        disabled: '#F8F7FF',        // Very light disabled
      },
    }
  },

  // 🎨 MODERN DASHBOARD THEME - New colorful card-based dashboard design
  modernDashboard: {
    // Main colors - clean and modern
    background: '#F8FAFC',            // Light neutral gray (matches our dashboards)
    text: '#1F2937',                  // Dark gray text for readability
    primary: '#3B82F6',               // Vibrant blue primary
    secondary: '#F1F5F9',             // Light slate for surfaces
    hover: '#E2E8F0',                 // Light hover state
    pressed: '#2563EB',               // Darker blue pressed
    disabled: '#E5E7EB',              // Light gray disabled
    accent: '#8B5CF6',                // Purple accent
    danger: '#EF4444',                // Red for danger
    dangerHover: '#DC2626',           // Darker red hover
    success: '#10B981',               // Emerald green for success
    successHover: '#059669',          // Darker green hover
    warning: '#F59E0B',               // Amber warning
    
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
    },
    
    button: {
      primary: {
        bg: '#3B82F6',                // Blue primary
        text: '#FFFFFF',
        hover: '#2563EB',
        pressed: '#1D4ED8',
        disabled: '#E5E7EB',
        solidBg: '#3B82F6',
        solidHover: '#2563EB',
      },
      secondary: {
        bg: '#F1F5F9',
        text: '#475569',
        hover: '#E2E8F0',
        pressed: '#CBD5E1',
        disabled: '#F9FAFB',
      },
      danger: {
        bg: 'rgba(239, 68, 68, 0.13)',
        text: '#EF4444',
        hover: 'rgba(239, 68, 68, 0.27)',
        pressed: '#DC2626',
        disabled: '#E5E7EB',
        solidBg: '#EF4444',
        solidText: '#FFFFFF',
        solidHover: '#DC2626',
      },
      success: {
        bg: '#10B981',
        text: '#FFFFFF',
        hover: '#059669',
        pressed: '#047857',
        disabled: '#D1D5DB',
      },
      warning: {
        bg: 'rgba(245, 158, 11, 0.13)',
        text: '#F59E0B',
        hover: 'rgba(245, 158, 11, 0.27)',
        pressed: '#D97706',
        disabled: '#E5E7EB',
      },
      utility: {
        bg: '#F3F4F6',
        text: '#374151',
        hover: '#E5E7EB',
        pressed: '#D1D5DB',
        disabled: '#F9FAFB',
      },
    },
    
    components: {
      card: {
        background: '#FFFFFF',
        border: '#E5E7EB',
        hover: '#F9FAFB',
        shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        hoverShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      input: {
        background: '#FFFFFF',
        border: '#D1D5DB',
        focus: '#3B82F6',
        focusRing: '0 0 0 3px rgba(59, 130, 246, 0.1)',
        placeholder: '#9CA3AF',
        disabled: '#F9FAFB',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.5)',
        background: '#FFFFFF',
        border: '#E5E7EB',
      },
      toggleIconButton: {
        background: '#F3F4F6',
        text: '#6B7280',
        hover: '#E5E7EB',
        pressed: '#D1D5DB',
        disabled: '#F9FAFB',
      },
      status: {
        pending: {
          bg: 'rgba(245, 158, 11, 0.13)',
          text: '#F59E0B',
          border: 'rgba(245, 158, 11, 0.3)',
        },
        accepted: {
          bg: 'rgba(59, 130, 246, 0.13)',
          text: '#3B82F6',
          border: 'rgba(59, 130, 246, 0.3)',
        },
        preparing: {
          bg: 'rgba(139, 92, 246, 0.13)',
          text: '#8B5CF6',
          border: 'rgba(139, 92, 246, 0.3)',
        },
        ready: {
          bg: 'rgba(16, 185, 129, 0.13)',
          text: '#10B981',
          border: 'rgba(16, 185, 129, 0.3)',
        },
        completed: {
          bg: 'rgba(107, 114, 128, 0.13)',
          text: '#6B7280',
          border: 'rgba(107, 114, 128, 0.3)',
        },
        cancelled: {
          bg: 'rgba(239, 68, 68, 0.13)',
          text: '#EF4444',
          border: 'rgba(239, 68, 68, 0.3)',
        },
      },
    }
  },

  // 🍃 VENDOR ORDERS THEME - Professional soft pastels for vendor/steward/admin pages
  vendorOrders: {
    // Main colors - soft, pastel foundation
    background: '#F8F9FA',            // Light gray-white background (Google News style)
    text: '#1E293B',                  // Dark slate text for readability
    primary: '#0F766E',               // Teal-700 (professional, not too bold)
    primaryLight: '#0F766E15',        // Light teal for backgrounds
    primaryHover: '#115E59',          // Teal-800 for hover
    secondary: '#F3F4F6',             // Very light gray for surfaces
    hover: '#F0FDFA',                 // Teal-50 hover
    pressed: '#134E4A',               // Teal-900 pressed
    disabled: '#E5E7EB',              // Light gray disabled
    accent: '#14B8A6',                // Teal-500 accent (lighter complement)
    danger: '#EF4444',                // Red for danger/cancel
    dangerHover: '#DC2626',           // Darker red hover
    success: '#10B981',               // Emerald green for success
    successHover: '#059669',          // Darker green hover
    warning: '#F59E0B',               // Amber warning
    
    // Gray palette for various UI elements
    gray: {
      50: '#F9FAFB',                  // Almost white
      100: '#F3F4F6',                 // Very light gray
      200: '#E5E7EB',                 // Light gray
      300: '#D1D5DB',                 // Medium-light gray
      500: '#6B7280',                 // Medium gray
      600: '#4B5563',                 // Dark gray
      700: '#374151',                 // Very dark gray
      800: '#1F2937',                 // Almost black
    },
    
    // Button styles - comprehensive coverage of all button patterns
    button: {
      // Primary buttons - TEAL for most action buttons (Submit, Save, Confirm, etc.)
      primary: {
        bg: '#0F766E',                // Teal-700 — professional contrast for CTAs
        text: '#FFFFFF',              // White text
        hover: '#115E59',             // Teal-800 hover
        pressed: '#134E4A',           // Teal-900 pressed
        disabled: '#E5E7EB',          // Light gray disabled
        // Solid variants
        solidBg: '#0F766E',           // Solid teal — buttons stay bold
        solidText: '#FFFFFF',         // White text
        solidHover: '#115E59',        // Teal-800 hover
      },

      // Secondary buttons (light backgrounds with colored borders/text)
      secondary: {
        bg: '#F0FDFA',                // Teal-50 background
        text: '#0F766E',              // Teal-700 text
        hover: '#CCFBF1',             // Teal-100 hover
        pressed: '#115E59',           // Teal-800 pressed
        disabled: '#F9FAFB',          // Very light disabled
        // Transparent variants with borders
        transparentBg: 'rgba(15, 118, 110, 0.08)',  // Teal-700 with 8% opacity
        border: '#14B8A6',            // Teal-500 border (softer)
      },
      
      // Danger buttons (soft red backgrounds)
      danger: {
        bg: 'rgba(239, 68, 68, 0.13)', // Soft red with transparency (#EF444422)
        text: '#EF4444',              // Red text
        hover: 'rgba(239, 68, 68, 0.27)', // Darker red hover (#EF444444)
        pressed: '#DC2626',           // Solid red pressed
        disabled: '#E5E7EB',          // Light gray disabled
        // Solid variants
        solidBg: '#EF4444',           // Solid red background
        solidText: '#FFFFFF',         // White text on solid
        solidHover: '#DC2626',        // Darker red hover
      },
      
      // Success/Confirm buttons (green) - ALSO USED FOR "ADD" BUTTONS
      success: {
        bg: '#065F46',                // Dark green (for Add Item, Add Order, etc.)
        text: '#FFFFFF',              // White text
        hover: '#047857',             // Lighter green hover
        pressed: '#064E3B',           // Darker green pressed
        disabled: '#D1D5DB',          // Gray disabled
        // Gradient variant for Add buttons
        gradient: 'linear-gradient(135deg, #065F46 0%, #047857 100%)',
        gradientHover: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
        // Transparent variants
        transparentBg: 'rgba(6, 95, 70, 0.13)', // Green with transparency
        border: '#065F46',            // Green border
      },
      
      // Warning buttons (yellow/amber)
      warning: {
        bg: 'rgba(245, 158, 11, 0.13)', // Soft amber with transparency
        text: '#F59E0B',              // Amber text
        hover: 'rgba(245, 158, 11, 0.27)', // Darker amber hover
        pressed: '#D97706',           // Solid amber pressed
        disabled: '#E5E7EB',          // Light gray disabled
        // Solid variants
        solidBg: '#F59E0B',           // Solid amber
        solidText: '#FFFFFF',         // White text
        solidHover: '#D97706',        // Darker amber hover
      },
      
      // Utility buttons (edit/view/close)
      utility: {
        bg: '#F3F4F6',                // Light gray background
        text: '#374151',              // Dark gray text
        hover: '#E5E7EB',             // Darker gray hover
        pressed: '#D1D5DB',           // Even darker pressed
        disabled: '#F9FAFB',          // Very light disabled
      },
    },
    
    // Component-specific styling
    components: {
      // Card styling (orders, menu items, etc.)
      card: {
        background: '#FFFFFF',        // Pure white cards
        border: '#E5E7EB',            // Light gray border
        hover: '#F9FAFB',             // Very light hover
        shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // Subtle shadow
        hoverShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // Elevated shadow on hover
        // Gradient backgrounds for special cards
        gradient: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
      },
      
      // Input/form styling
      input: {
        background: '#FFFFFF',        // White input background
        border: '#D1D5DB',            // Gray border
        focus: '#1D4ED8',             // Blue focus border
        focusRing: '0 0 0 3px rgba(29, 78, 216, 0.1)', // Focus ring
        placeholder: '#9CA3AF',       // Gray placeholder text
        disabled: '#F9FAFB',          // Light disabled background
      },
      
      // Modal/dialog styling
      modal: {
        overlay: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black overlay
        background: '#FFFFFF',        // White modal background
        border: '#E5E7EB',            // Light border
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // Large shadow
      },
      
      // Toggle/icon buttons
      toggleIconButton: {
        background: '#F3F4F6',        // Light gray background
        text: '#6B7280',              // Medium gray icon color
        hover: '#E5E7EB',             // Darker gray hover
        pressed: '#D1D5DB',           // Even darker pressed
        disabled: '#F9FAFB',          // Very light disabled
      },
      
      // Status badges (PENDING, ACCEPTED, PREPARING, etc.)
      status: {
        pending: {
          bg: 'rgba(245, 158, 11, 0.13)',  // Soft amber (#F59E0B22)
          text: '#F59E0B',            // Amber text
          border: 'rgba(245, 158, 11, 0.3)', // Amber border
        },
        accepted: {
          bg: 'rgba(29, 78, 216, 0.13)',   // Soft blue (#1D4ED822)
          text: '#1D4ED8',            // Blue text
          border: 'rgba(29, 78, 216, 0.3)', // Blue border
        },
        preparing: {
          bg: 'rgba(147, 51, 234, 0.13)',  // Soft purple (#9333EA22)
          text: '#9333EA',            // Purple text
          border: 'rgba(147, 51, 234, 0.3)', // Purple border
        },
        ready: {
          bg: 'rgba(16, 185, 129, 0.13)',  // Soft green (#10B98122)
          text: '#10B981',            // Green text
          border: 'rgba(16, 185, 129, 0.3)', // Green border
        },
        completed: {
          bg: 'rgba(107, 114, 128, 0.13)', // Soft gray (#6B728022)
          text: '#6B7280',            // Gray text
          border: 'rgba(107, 114, 128, 0.3)', // Gray border
        },
        cancelled: {
          bg: 'rgba(239, 68, 68, 0.13)',   // Soft red (#EF444422)
          text: '#EF4444',            // Red text
          border: 'rgba(239, 68, 68, 0.3)', // Red border
        },
      },
      
      // Table styling
      table: {
        header: '#F9FAFB',            // Light gray header
        border: '#E5E7EB',            // Light gray border
        hover: '#F3F4F6',             // Row hover
        stripe: '#F9FAFB',            // Striped row background
      },
      
      // Badge/pill styling
      badge: {
        primary: {
          bg: '#DBEAFE',              // Light blue background
          text: '#1E40AF',            // Dark blue text
        },
        success: {
          bg: '#D1FAE5',              // Light green background
          text: '#065F46',            // Dark green text
        },
        warning: {
          bg: '#FEF3C7',              // Light amber background
          text: '#92400E',            // Dark amber text
        },
        danger: {
          bg: '#FEE2E2',              // Light red background
          text: '#991B1B',            // Dark red text
        },
        info: {
          bg: '#E0E7FF',              // Light indigo background
          text: '#3730A3',            // Dark indigo text
        },
      },
      
      // Divider/separator
      divider: '#E5E7EB',             // Light gray divider
      
      // Loading/skeleton
      skeleton: {
        base: '#E5E7EB',              // Light gray base
        shimmer: '#F3F4F6',           // Lighter gray shimmer
      },
    }
  },

  // 🌿 SCANDINAVIAN WARM THEME - Warm, organic aesthetics with sage green accents
  basil: {
    background: '#FAFBF9',
    text: '#262C26',
    primary: '#2F7D5B',
    secondary: '#FFFFFF',
    hover: '#266A4C',
    pressed: '#1E5540',
    disabled: '#E8EBE7',
    accent: '#2F7D5B',
    danger: '#D2453C',
    dangerHover: '#B5372F',
    success: '#1E8E5A',
    successHover: '#167048',
    warning: '#E0891E',
    gray: {
      50: '#F4F6F3',
      100: '#E8EBE7',
      200: '#D6DBD3',
      300: '#B9C0B5',
      400: '#929B8D',
      500: '#6B7469',
      600: '#515A50',
      700: '#3A423A',
      800: '#262C26',
      900: '#171B17',
    },
    button: {
      primary: { bg: '#2F7D5B', text: '#FFFFFF', hover: '#266A4C', pressed: '#1E5540', disabled: '#E8EBE7' },
      secondary: { bg: '#F4F6F3', text: '#262C26', hover: '#E8EBE7', pressed: '#D6DBD3', disabled: '#F4F6F3' },
      utility: { bg: '#F4F6F3', text: '#515A50', hover: '#E8EBE7', pressed: '#EEF6F1', disabled: '#E8EBE7' },
      danger: { bg: '#D2453C', text: '#FFFFFF', hover: '#B5372F', pressed: '#9A2E27', disabled: '#E8EBE7' },
    },
    components: {
      card: { background: '#FFFFFF', border: '#D6DBD3', hover: '#F4F6F3' },
      input: { background: '#FFFFFF', border: '#D6DBD3', focus: '#2F7D5B' },
      modal: { overlay: 'rgba(23, 27, 23, 0.5)', background: '#FFFFFF' },
      toggleIconButton: { background: '#F4F6F3', text: '#515A50', hover: '#E8EBE7', pressed: '#EEF6F1', disabled: '#E8EBE7' },
    },
  },
  basilDark: {
    background: '#12160F',
    text: '#EAEFE7',
    primary: '#4E9E77',
    secondary: '#1A1F17',
    hover: '#5FB088',
    pressed: '#79BC98',
    disabled: '#2A3126',
    accent: '#4E9E77',
    danger: '#EC8A82',
    dangerHover: '#D2453C',
    success: '#69C795',
    successHover: '#1E8E5A',
    warning: '#E9B872',
    gray: {
      50: '#141810',
      100: '#2A3126',
      200: '#353E30',
      300: '#47533F',
      400: '#5B6553',
      500: '#8B9484',
      600: '#B4BDAC',
      700: '#CDD5C6',
      800: '#EAEFE7',
      900: '#F1F4EE',
    },
    button: {
      primary: { bg: '#4E9E77', text: '#0F2C21', hover: '#5FB088', pressed: '#79BC98', disabled: '#2A3126' },
      secondary: { bg: '#141810', text: '#EAEFE7', hover: '#222820', pressed: '#1E3327', disabled: '#141810' },
      utility: { bg: '#141810', text: '#B4BDAC', hover: '#222820', pressed: '#1E3327', disabled: '#2A3126' },
      danger: { bg: '#D2453C', text: '#EAEFE7', hover: '#B5372F', pressed: '#9A2E27', disabled: '#2A3126' },
    },
    components: {
      card: { background: '#1A1F17', border: '#353E30', hover: '#222820' },
      input: { background: '#1A1F17', border: '#353E30', focus: '#79BC98' },
      modal: { overlay: 'rgba(0, 0, 0, 0.6)', background: '#1A1F17' },
      toggleIconButton: { background: '#141810', text: '#B4BDAC', hover: '#222820', pressed: '#1E3327', disabled: '#2A3126' },
    },
  },
  saffron: {
    // Aubergine & Saffron (owner-selected 2026-08-05): warm ivory content,
    // saffron-ochre actions, aubergine reserved for the shell (rail/band).
    background: '#FAF8F4',
    text: '#2B2620',
    primary: '#A66A10',
    secondary: '#EDE8DF',
    hover: '#8F5A0E',
    pressed: '#7A4D0C',
    disabled: '#D9CFBE',
    accent: '#7A4463',
    danger: '#DC2626',
    dangerHover: '#B91C1C',
    success: '#16A34A',
    successHover: '#15803D',
    warning: '#D97706',
    gray: {
      50: '#FAF8F4',
      100: '#F1EDE5',
      200: '#E3DCCF',
      300: '#CFC5B2',
      400: '#A89A82',
      500: '#7E7261',
      600: '#5D5347',
      700: '#453E35',
      800: '#2B2620',
      900: '#1B1712',
    },
    button: {
      primary: { bg: '#A66A10', text: '#FFFFFF', hover: '#8F5A0E', pressed: '#7A4D0C', disabled: '#E3DCCF' },
      secondary: { bg: '#F1EDE5', text: '#2B2620', hover: '#E3DCCF', pressed: '#D9CFBE', disabled: '#F1EDE5' },
      utility: { bg: '#F1EDE5', text: '#5D5347', hover: '#E3DCCF', pressed: '#F7EFDF', disabled: '#E3DCCF' },
      danger: { bg: '#DC2626', text: '#FFFFFF', hover: '#B91C1C', pressed: '#991B1B', disabled: '#E3DCCF' },
    },
    components: {
      card: { background: '#FFFFFF', border: '#E3DCCF', hover: '#F1EDE5' },
      input: { background: '#FFFFFF', border: '#E3DCCF', focus: '#A66A10' },
      modal: { overlay: 'rgba(27, 23, 18, 0.5)', background: '#FFFFFF' },
      toggleIconButton: { background: '#F1EDE5', text: '#5D5347', hover: '#E3DCCF', pressed: '#F7EFDF', disabled: '#E3DCCF' },
    },
  },
  scandinavianWarm: {
    background: '#F7F6F3',              // Parchment background
    text: '#2D2D2D',                    // Dark text
    primary: '#7C9070',                 // Sage green
    secondary: '#E8E6E1',              // Border/muted
    hover: '#6B7D60',                   // Darker sage
    pressed: '#5A6C51',                 // Deep sage
    disabled: '#B8C4B0',               // Muted sage
    accent: '#7C9070',                  // Sage green accent
    danger: '#DC2626',                  // Red
    dangerHover: '#B91C1C',            // Darker red
    success: '#16A34A',                 // Green
    successHover: '#15803D',           // Darker green
    warning: '#D97706',                 // Amber

    gray: {
      50: '#F7F6F3',                    // Parchment
      100: '#F0EDE8',                   // Input/nav bg
      200: '#E8E6E1',                   // Border
      300: '#D4D1CC',                   // Muted border
      500: '#8A8A8A',                   // Secondary text
      600: '#6B6B6B',                   // Dark secondary
      700: '#4A4A4A',                   // Dark text
      800: '#2D2D2D',                   // Primary text
    },

    button: {
      primary: {
        bg: '#7C9070',                  // Sage green
        text: '#FFFFFF',
        hover: '#6B7D60',              // Darker sage
        pressed: '#5A6C51',            // Deep sage
        disabled: '#B8C4B0',           // Muted sage
        solidBg: '#7C9070',
        solidText: '#FFFFFF',
        solidHover: '#6B7D60',
      },
      secondary: {
        bg: '#FFFFFF',                  // White fill
        text: '#7C9070',               // Sage text
        hover: '#F0EDE8',              // Warm hover
        pressed: '#E8E6E1',
        disabled: '#F7F6F3',
        transparentBg: 'rgba(124, 144, 112, 0.08)',
        border: '#7C9070',             // Sage border
      },
      danger: {
        bg: '#FEE2E2',                 // Light red bg
        text: '#DC2626',               // Red text
        hover: '#FECACA',
        pressed: '#B91C1C',
        disabled: '#E8E6E1',
        solidBg: '#DC2626',
        solidText: '#FFFFFF',
        solidHover: '#B91C1C',
      },
      success: {
        bg: '#DCFCE7',                 // Light green bg
        text: '#16A34A',               // Green text
        hover: '#BBF7D0',
        pressed: '#15803D',
        disabled: '#E8E6E1',
        transparentBg: 'rgba(22, 163, 74, 0.08)',
        border: '#16A34A',
      },
      warning: {
        bg: '#FEF3C7',                 // Light amber bg
        text: '#D97706',               // Amber text
        hover: '#FDE68A',
        pressed: '#B45309',
        disabled: '#E8E6E1',
        solidBg: '#D97706',
        solidText: '#FFFFFF',
        solidHover: '#B45309',
      },
      utility: {
        bg: '#F0EDE8',                 // Input bg
        text: '#4A4A4A',
        hover: '#E8E6E1',
        pressed: '#D4D1CC',
        disabled: '#F7F6F3',
      },
      add: {
        bg: '#7C9070',                 // Sage green
        text: '#FFFFFF',
        hover: '#6B7D60',
        pressed: '#5A6C51',
        shadow: '0 2px 4px rgba(124, 144, 112, 0.3)',
        hoverShadow: '0 4px 8px rgba(124, 144, 112, 0.4)',
      },
      floating: {
        bg: '#7C9070',
        text: '#FFFFFF',
        hover: '#6B7D60',
        shadow: '0 4px 12px rgba(124, 144, 112, 0.3)',
        hoverShadow: '0 6px 16px rgba(124, 144, 112, 0.4)',
      },
    },

    components: {
      card: {
        background: '#FFFFFF',
        border: '#E8E6E1',
        hover: '#F7F6F3',
        shadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        hoverShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      input: {
        background: '#F0EDE8',          // Warm input bg
        border: '#E8E6E1',
        focus: '#7C9070',               // Sage focus
        focusRing: '0 0 0 3px rgba(124, 144, 112, 0.2)',
        placeholder: '#8A8A8A',
        disabled: '#F7F6F3',
      },
      modal: {
        overlay: 'rgba(0, 0, 0, 0.5)',
        background: '#FFFFFF',
        border: '#E8E6E1',
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      },
      toggleIconButton: {
        background: '#F0EDE8',
        text: '#2D2D2D',
        hover: '#E8E6E1',
        pressed: '#D4D1CC',
        disabled: '#F0EDE8',
      },
      status: {
        pending: { bg: '#DBEAFE', text: '#2563EB', border: 'rgba(37, 99, 235, 0.3)' },
        accepted: { bg: '#DBEAFE', text: '#5B9BD5', border: 'rgba(91, 155, 213, 0.3)' },
        preparing: { bg: '#DBEAFE', text: '#5B9BD5', border: 'rgba(91, 155, 213, 0.3)' },
        ready: { bg: '#DCFCE7', text: '#16A34A', border: 'rgba(22, 163, 74, 0.3)' },
        completed: { bg: '#DCFCE7', text: '#16A34A', border: 'rgba(22, 163, 74, 0.3)' },
        served: { bg: '#DCFCE7', text: '#16A34A', border: 'rgba(22, 163, 74, 0.3)' },
        cancelled: { bg: '#FEE2E2', text: '#DC2626', border: 'rgba(220, 38, 38, 0.3)' },
        available: { bg: '#DCFCE7', text: '#16A34A', border: 'rgba(22, 163, 74, 0.3)' },
        occupied: { bg: '#FEF3C7', text: '#D97706', border: 'rgba(217, 119, 6, 0.3)' },
        reserved: { bg: '#FEE2E2', text: '#DC2626', border: 'rgba(220, 38, 38, 0.3)' },
        cleaning: { bg: '#F0EDE8', text: '#8A8A8A', border: 'rgba(138, 138, 138, 0.3)' },
        urgent: { bg: 'rgba(212, 132, 94, 0.15)', text: '#D4845E', border: 'rgba(212, 132, 94, 0.3)' },
      },
      table: {
        header: '#F0EDE8',
        border: '#E8E6E1',
        hover: '#F7F6F3',
        stripe: '#FAFAF8',
      },
      badge: {
        primary: { bg: '#DCFCE7', text: '#16A34A' },
        success: { bg: '#DCFCE7', text: '#16A34A' },
        warning: { bg: '#FEF3C7', text: '#D97706' },
        danger: { bg: '#FEE2E2', text: '#DC2626' },
        info: { bg: '#DBEAFE', text: '#2563EB' },
      },
      divider: '#E8E6E1',
      skeleton: {
        base: '#F0EDE8',
        shimmer: '#F7F6F3',
      },
      bottomNav: {
        background: '#FFFFFF',
        backdropBlur: 'none',
        border: '#E8E6E1',
        activeIndicator: '#7C9070',
        inactiveIcon: '#8A8A8A',
        badge: '#D4845E',
      },
      bottomSheet: {
        background: '#FFFFFF',
        handle: '#D4D1CC',
        shadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
      },
      search: {
        background: '#F0EDE8',
        placeholder: '#8A8A8A',
        text: '#2D2D2D',
        icon: '#8A8A8A',
      },
      header: {
        gradient: '#7C9070',
        text: '#2D2D2D',
        subtext: '#8A8A8A',
      },
      pill: {
        active: { bg: '#7C9070', text: '#FFFFFF', shadow: '0 1px 3px rgba(124, 144, 112, 0.3)' },
        inactive: { bg: '#F0EDE8', text: '#8A8A8A', hover: '#E8E6E1' },
      },
      menuItem: {
        imagePlaceholder: '#F0EDE8',
        popular: { ring: '#D4845E', badge: '#D4845E', icon: '#D4845E' },
        favorite: { active: '#D4845E', inactive: '#8A8A8A' },
        time: { icon: '#8A8A8A', text: '#8A8A8A' },
      },
      quantityControl: {
        container: '#F0EDE8',
        button: { bg: '#FFFFFF', text: '#7C9070', hover: '#F0EDE8', shadow: '0 1px 2px rgba(0,0,0,0.05)' },
        addButton: { bg: '#7C9070', text: '#FFFFFF', hover: '#6B7D60' },
      },
    }
  }
};

type ThemeContextType = {
  theme: typeof themes.light;
  setTheme: (theme: typeof themes.light) => void;
  toggleTheme: () => void;
  selectTheme: (themeName: string) => void;
  currentThemeName: string;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Presets whose values agree with the Tier-2 tokens in `styles/tokens.css`.
 *
 * This provider never writes `--action` (or any Tier-2 token) — those are FIXED
 * at saffron ochre #A66A10. It only writes the legacy `--color-*` bridge from
 * the active preset. So a preset is safe ONLY if its `button.primary.bg`
 * already equals the token; otherwise the page renders legacy-coloured buttons
 * beside token-coloured ones. Measured 2026-08-07: `saffron` matches (#A66A10),
 * `basil` does not (#2F7D5B), and neither do the other 22.
 *
 * That is the whole "why are these buttons blue" bug: a device holding an old
 * `themeName` kept painting from a preset the tokens no longer agree with.
 */
const DESIGN_SYSTEM_THEMES = new Set(['saffron']);
const DEFAULT_THEME = 'saffron'; // Aubergine & Saffron (owner-selected 2026-08-05)
const THEME_STORAGE_KEY = 'themeName';
// One-time marker: once set, the off-token heal never runs again, so a user's
// explicit theme choice is respected on subsequent loads. Bump the suffix only
// if a future token freeze needs another forced heal.
const THEME_HEAL_FLAG = 'themeName:offTokenHealed:v1';

const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // Load saved theme from localStorage
  const getSavedTheme = () => {
    if (typeof window === 'undefined') return DEFAULT_THEME;

    let saved = localStorage.getItem(THEME_STORAGE_KEY);

    // ONE-TIME migration (flag-gated): before the Tier-2 token freeze, a device
    // could hold an off-token preset (e.g. 'scandinavianWarm') that repaints the
    // app with legacy colours next to token-coloured ones — the "why are these
    // buttons blue" bug. Heal such stale values ONCE. After the flag is set we
    // NEVER coerce again, so a user's deliberate ThemeSelector pick survives
    // reloads instead of being silently discarded on every load.
    try {
      if (!localStorage.getItem(THEME_HEAL_FLAG)) {
        if (saved && !DESIGN_SYSTEM_THEMES.has(saved)) {
          localStorage.setItem(THEME_STORAGE_KEY, DEFAULT_THEME);
          saved = DEFAULT_THEME;
        }
        localStorage.setItem(THEME_HEAL_FLAG, '1');
      }
    } catch { /* private browsing — skip migration, honour whatever is saved */ }

    return saved && themes[saved] ? saved : DEFAULT_THEME;
  };
  
  const initialThemeName = getSavedTheme();
  const [theme, setTheme] = useState(themes[initialThemeName] || themes.basil);
  const [currentThemeName, setCurrentThemeName] = useState<string>(initialThemeName);
  const { mode: appColorMode } = useColorModeSafe();
  // App-level dark mode swaps the Basil preset for its dark twin. Named vendor
  // presets (customer-facing branding) are left alone on purpose.
  const effectiveTheme = (appColorMode === 'dark' && currentThemeName === 'basil')
    ? themes.basilDark
    : theme;
  
  // Sync CSS variables (design tokens) with current theme
  React.useEffect(() => {
    const root = document.documentElement;
    // Map CSS variable names to theme values
    const vars: Record<string, string> = {
      '--color-text-primary': effectiveTheme.text,
      '--color-text-secondary': effectiveTheme.gray[600],
      '--color-text-link': effectiveTheme.primary,
      '--color-background-default': effectiveTheme.background,
      '--color-background-surface': effectiveTheme.components.card.background,
      '--color-background-primary': effectiveTheme.primary,
      '--color-background-on-primary': effectiveTheme.button.primary.text,
      '--color-border-default': effectiveTheme.components.card.border,
      '--color-brand-primary': effectiveTheme.button.primary.solidBg || effectiveTheme.button.primary.bg, // Use solid fallback for gradients
      '--color-brand-primary-hover': effectiveTheme.button.primary.solidHover || effectiveTheme.button.primary.hover,
      '--color-button-secondary': effectiveTheme.button.secondary.bg,
      '--color-button-secondary-hover': effectiveTheme.button.secondary.hover,
      '--color-button-utility': effectiveTheme.button.utility?.bg || effectiveTheme.button.secondary.bg, // Edit/delete buttons
      '--color-button-utility-text': effectiveTheme.button.utility?.text || effectiveTheme.button.secondary.text,
      '--color-button-utility-hover': effectiveTheme.button.utility?.hover || effectiveTheme.button.secondary.hover,
      '--color-button-utility-pressed': effectiveTheme.button.utility?.pressed || effectiveTheme.button.secondary.hover,
      '--color-button-utility-disabled': effectiveTheme.button.utility?.disabled || effectiveTheme.button.secondary.disabled || '#E0E0E0',
      // Apply Google News-themed danger button styling
      '--color-button-danger': effectiveTheme.button.danger.bg, // Light red bg (#FCE8E6 for Google News)
      '--color-button-danger-text': effectiveTheme.button.danger.text, // Red text (#D93025 for Google News)
      '--color-button-danger-hover': effectiveTheme.button.danger.hover, // Lighter red hover (#F8DAD9)
      '--color-button-danger-pressed': effectiveTheme.button.danger.pressed || effectiveTheme.button.danger.hover,
      // Using utility button styles for toggle icon buttons
      '--color-toggle-icon-bg': effectiveTheme.button.utility?.bg || effectiveTheme.components.toggleIconButton?.background || effectiveTheme.button.secondary.bg || '#F8EEDC',
      '--color-toggle-icon-text': effectiveTheme.button.utility?.text || effectiveTheme.components.toggleIconButton?.text || effectiveTheme.button.secondary.text || '#3E3E3E',
      '--color-toggle-icon-hover': effectiveTheme.button.utility?.hover || effectiveTheme.components.toggleIconButton?.hover || effectiveTheme.button.secondary.hover || '#F3E7D5',
      '--color-tab-active': effectiveTheme.accent, // Controls category tabs (#5D6A4E)
      '--color-brand-teal': effectiveTheme.success,
      '--color-brand-blue': effectiveTheme.hover,
      '--color-brand-yellow': effectiveTheme.secondary,
      '--color-brand-red': effectiveTheme.danger,
      // Modern Flow specific variables
      '--gradient-primary': effectiveTheme.button.primary.bg,
      '--gradient-primary-hover': effectiveTheme.button.primary.hover,
      '--gradient-header': effectiveTheme.components?.header?.gradient || effectiveTheme.button.primary.bg,
      '--color-accent': effectiveTheme.accent,
      '--color-success': effectiveTheme.success,
      '--color-warning': effectiveTheme.warning,
      '--shadow-card': effectiveTheme.components?.card?.shadow || '0 1px 3px rgba(0, 0, 0, 0.1)',
      '--shadow-card-hover': effectiveTheme.components?.card?.hoverShadow || '0 4px 6px rgba(0, 0, 0, 0.1)',
    };
    Object.entries(vars).forEach(([name, value]) => {
      root.style.setProperty(name, value);
    });

    // Scandinavian Warm: set font-family and accent CSS variables
    if (currentThemeName === 'scandinavianWarm') {
      root.style.setProperty('--font-family-heading', "'Fraunces', Georgia, serif");
      root.style.setProperty('--font-family-body', "'Plus Jakarta Sans', system-ui, sans-serif");
      root.style.setProperty('--color-accent', '#D4845E');
      root.style.setProperty('--color-info', '#5B9BD5');
    } else {
      root.style.setProperty('--font-family-heading', 'var(--font-family-base)');
      root.style.setProperty('--font-family-body', 'var(--font-family-base)');
    }

    // Update meta theme-color for mobile browser chrome
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', effectiveTheme.background);
    }
  }, [effectiveTheme, currentThemeName]);

  const selectTheme = (themeName: string) => {
    // Allow any valid theme to be selected
    if (themes[themeName]) {
      // Save theme preference to localStorage
      localStorage.setItem('themeName', themeName);
      setTheme(themes[themeName]);
      setCurrentThemeName(themeName);
      console.log('🎨 Theme changed to:', themeName);
    }
  };

  const toggleTheme = () => {
    const themeKeys = Object.keys(themes);
    const currentIndex = themeKeys.findIndex((key) => themes[key] === theme);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    const nextThemeName = themeKeys[nextIndex];
    
    selectTheme(nextThemeName);
  };

  return (
    <ThemeContext.Provider value={{ theme: effectiveTheme, setTheme, toggleTheme, selectTheme, currentThemeName }}>
      <div 
        data-theme={currentThemeName}
        style={{ 
          backgroundColor: theme.background, 
          color: theme.text,
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          ...(currentThemeName === 'gradient' ? {
            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
          } : currentThemeName === 'glassmorphism' ? {
            background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f172a 100%)',
          } : {})
        }}
        className={currentThemeName === 'gradient' ? 'gradient-theme' : currentThemeName === 'glassmorphism' ? 'glassmorphism-theme' : ''}
      >
        {(currentThemeName === 'gradient' || currentThemeName === 'glassmorphism') && (
          <>
            <div className={currentThemeName === 'gradient' ? 'gradient-orb-1' : 'glassmorphism-orb-1'} />
            <div className={currentThemeName === 'gradient' ? 'gradient-orb-2' : 'glassmorphism-orb-2'} />
          </>
        )}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {children}
        </div>
      </div>
    </ThemeContext.Provider>
  );
};

// Export CSS variable based theme that directly references the style-dictionary tokens
const cssVarTheme = {
  background: 'var(--bg-app)',
  text: '#262C26',
  primary: '#2F7D5B',
  secondary: '#FFFFFF',
  hover: '#266A4C',
  danger: '#D2453C',
  dangerHover: '#D2453C',
  success: '#1E8E5A',
  successHover: '#1E8E5A',
  accent: '#2F7D5B',
  gray: themes.light.gray,
  button: {
    primary: {
      bg: '#2F7D5B',
      text: '#FFFFFF',
      hover: '#266A4C',
    },
    secondary: {
      bg: '#F4F6F3',
      text: '#262C26',
      hover: '#F4F6F3',
    },
    utility: {
      bg: '#F4F6F3',
      text: '#515A50',
      hover: '#F4F6F3',
      pressed: '#EEF6F1',
      disabled: '#E8EBE7',
    },
    danger: {
      bg: '#D2453C',
      text: '#FFFFFF',
      hover: '#D2453C',
      pressed: '#D2453C',
    }
  },
  components: {
    card: {
      background: 'var(--bg-surface)',
      border: 'var(--border-default)',
      hover: '#F4F6F3',
    },
    input: {
      background: 'var(--bg-surface)',
      border: 'var(--border-default)',
      focus: '#2F7D5B',
    },
    modal: {
      overlay: themes.light.components.modal.overlay,
      background: 'var(--bg-surface)',
    },
    toggleIconButton: {
      background: 'var(--bg-sunken)',
      text: '#515A50',
      hover: '#F4F6F3',
      pressed: '#EEF6F1',
      disabled: '#E8EBE7',
    }
  }
};
// Attach the CSS-variable-based theme so it can be selected programmatically
themes.css = cssVarTheme;

// Provider-optional: leaf components (dialogs mounted by other providers,
// unit tests) fall back to the Basil preset instead of throwing.
const themeFallback = {
  theme: undefined as never, // set below — themes not yet declared at module top
  setTheme: () => {},
  toggleTheme: () => {},
  selectTheme: () => {},
  currentThemeName: 'basil',
};
const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context) return context;
  if (!themeFallback.theme) (themeFallback as { theme: unknown }).theme = themes.basil;
  return themeFallback as ReturnType<typeof Object> & typeof themeFallback;
};
const useThemeSafe = useTheme;

// Status-based colors mapped to CSS variables for easy theme control
// Basil bridge: status chips read Tier-2 tokens, so they follow light/dark
// re-points with zero component changes (audit §8 requirement — KDS consumes
// these on dark panels).
const statusColors: Record<string, { bg: string; text: string }> = {
  PENDING: { bg: '#FCF1E0', text: '#8A5411' },
  ACCEPTED: { bg: '#E9F1FA', text: '#1E4E86' },
  PREPARING: { bg: '#2F7D5B', text: '#FFFFFF' },
  READY_FOR_PICKUP: { bg: '#E6F4EC', text: '#10603C' },
  COMPLETED: { bg: '#F4F6F3', text: '#515A50' },
  CANCELLED: { bg: '#D2453C', text: '#FFFFFF' },
};

// Export all hooks, components and variables
export { themes, ThemeContext, ThemeProvider, useTheme, useThemeSafe, statusColors };
